import { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Mail, Send, FileText } from "lucide-react";
import { motion } from "framer-motion";
import NewsletterManager from "../components/NewsletterManager";
import { TestimonyForm } from "../components/TestimonyForm";
import { ArticleUploadForm } from "../components/ArticleUploadForm";

const KeepInTouch = () => {
  useEffect(() => {
    document.title = "Keep in Touch – OFUESI";
    const desc = "Stay connected with OFUESI through our newsletter, share your testimony, or submit articles.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
  }, []);

  return (
    <div className="min-h-screen faith-pattern" style={{ background: "var(--background)" }}>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-ministry-cyan via-primary to-ministry-cyan py-10 relative">
        <div className="absolute inset-0 faith-pattern opacity-20"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto text-white space-y-6">
            <h1 className="text-4xl lg:text-6xl font-bold text-white drop-shadow-lg">Keep In Touch</h1>
            <p className="text-xl leading-relaxed text-white">
              Access our newsletter, share your testimony, or contribute articles to inspire the community.
            </p>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-10 relative faith-pattern">
        <div className="container mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Access our quarterly newsletter with ministry updates, testimonies, and upcoming events.
              </p>
            </div>
            <NewsletterManager />
          </motion.div>
        </div>
      </section>

      {/* Share Your Story Section */}
      <section className="py-20 bg-muted/30 relative">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Testimony Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="h-full elegant-shadow">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                      <Send className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle>Share Your Testimony</CardTitle>
                  </div>
                  <p className="text-muted-foreground">
                    Your story can inspire and encourage others in their faith journey. Share how God has worked in your
                    life.
                  </p>
                </CardHeader>
                <CardContent>
                  <TestimonyForm />
                </CardContent>
              </Card>
            </motion.div>

            {/* Article Upload Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Card className="h-full elegant-shadow">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle>Submit an Article</CardTitle>
                  </div>
                  <p className="text-muted-foreground">
                    Have insights to share? Submit your article for our KIT newsletter to reach the wider OFUESI
                    community.
                  </p>
                </CardHeader>
                <CardContent>
                  <ArticleUploadForm />
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KeepInTouch;
